import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Users,
  Crown,
  Wallet,
  DollarSign,
  MessageSquare,
  UserPlus,
  Coins,
  ArrowRight,
  Download,
  Sparkles,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

/**
 * MysticStat — velvet-glass KPI card with a subtle gold/violet aura on hover.
 * Accent = "gold" | "violet" | "emerald" | "danger" (default gold).
 */
function MysticStat({ label, value, icon: Icon, accent = "gold", suffix, testid, index = 0 }) {
  const accentMap = {
    gold:    { icon: "text-gold-antique", glow: "hover:shadow-[0_0_28px_-8px_rgba(212,175,55,0.55)]", chip: "from-gold-antique/25 to-transparent", border: "hover:border-gold-antique/40", num: "from-gold-pale via-gold-antique to-gold-dim" },
    violet:  { icon: "text-violet-mystic", glow: "hover:shadow-[0_0_28px_-8px_rgba(109,40,217,0.55)]", chip: "from-violet-mystic/25 to-transparent", border: "hover:border-violet-mystic/40", num: "from-violet-mystic via-gold-antique to-gold-pale" },
    emerald: { icon: "text-emerald-400", glow: "hover:shadow-[0_0_28px_-8px_rgba(52,211,153,0.45)]", chip: "from-emerald-500/20 to-transparent", border: "hover:border-emerald-500/40", num: "from-emerald-300 via-gold-antique to-gold-dim" },
    danger:  { icon: "text-red-400", glow: "hover:shadow-[0_0_28px_-8px_rgba(239,68,68,0.45)]", chip: "from-red-500/20 to-transparent", border: "hover:border-red-500/40", num: "from-red-300 via-gold-antique to-gold-dim" },
  };
  const a = accentMap[accent] || accentMap.gold;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.03 * index }}
      whileHover={{ y: -3 }}
      className={`group relative bg-black/70 backdrop-blur-xl border border-white/[0.06] ${a.border} rounded-lg p-5 transition-all ${a.glow} overflow-hidden`}
      data-testid={testid}
    >
      {/* Corner gilded accent */}
      <span className={`pointer-events-none absolute -top-px -left-px h-4 w-4 border-t border-l border-gold-antique/40 rounded-tl-lg`} />
      <span className={`pointer-events-none absolute -bottom-px -right-px h-4 w-4 border-b border-r border-gold-antique/40 rounded-br-lg`} />
      {/* Chip gradient at top */}
      <span className={`pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b ${a.chip} opacity-70`} />

      <div className="relative flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-zinc-500">
          {label}
        </div>
        <Icon size={16} strokeWidth={1.6} className={`${a.icon} drop-shadow-[0_0_6px_currentColor] opacity-90`} />
      </div>
      <div className={`relative font-topsecret text-3xl tracking-[0.03em] backlight-soft-gold`}>
        {value}
        {suffix && (
          <span className="ml-2 text-xs font-mono font-normal text-zinc-500 align-middle not-italic">
            {suffix}
          </span>
        )}
      </div>
    </motion.div>
  );
}

/**
 * MysticChartCard — glass container for recharts, with gold accents on axes.
 */
function MysticChartCard({ title, subtitle, icon: Icon, children, testid }) {
  return (
    <div className="relative bg-black/70 backdrop-blur-xl border border-white/[0.06] rounded-lg p-5 overflow-hidden" data-testid={testid}>
      <span className="pointer-events-none absolute -top-px -left-px h-4 w-4 border-t border-l border-gold-antique/50 rounded-tl-lg" />
      <span className="pointer-events-none absolute -bottom-px -right-px h-4 w-4 border-b border-r border-gold-antique/50 rounded-br-lg" />
      <div className="flex items-center justify-between mb-6 relative">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-gold-antique/60">
            {subtitle}
          </div>
          <h3 className="font-topsecret text-2xl text-zinc-100 mt-1 tracking-[0.03em] backlight-soft-gold">
            {title}
          </h3>
        </div>
        {Icon && <Icon size={16} strokeWidth={1.6} className="text-gold-antique/70" />}
      </div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);

  useEffect(() => {
    api.get("/stats/dashboard").then((r) => setStats(r.data)).catch(() => {});
    api.get("/stats/timeseries?days=14").then((r) => setSeries(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8 max-w-[1400px]" data-testid="dashboard-page">
      {/* Ritual header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] font-mono text-gold-antique/70 mb-2 flex items-center gap-2">
            <Sparkles size={11} className="text-gold-antique animate-gold-pulse" />
            · Sanctum · Command Room ·
          </div>
          <h1 className="font-topsecret text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-[0.04em] backlight-gold neon-flicker">
            PANEL DE CONTROL
          </h1>
          <p className="font-bingbam italic text-lg text-zinc-300/80 mt-4 max-w-xl leading-relaxed">
            El pulso de tu bot en tiempo real — usuarios, ingresos, créditos y operación,
            todo desde este altar central.
          </p>
        </div>
        {stats?.pending_payments > 0 && (
          <Link
            to="/admin/pagos"
            className="flex items-center gap-3 bg-gold-antique/10 text-gold-antique border border-gold-antique/40 rounded-md px-5 py-3 hover:bg-gold-antique/20 hover:shadow-[0_0_25px_rgba(212,175,55,0.5)] transition-all font-mono"
            data-testid="dashboard-pending-cta"
          >
            <span className="font-topsecret text-2xl backlight-gold">
              {stats.pending_payments}
            </span>
            <span className="text-xs uppercase tracking-[0.25em]">Pagos por aprobar</span>
            <ArrowRight size={14} />
          </Link>
        )}
      </div>

      {/* Gilded divider */}
      <div className="flex items-center gap-4">
        <span className="h-px flex-1 bg-gradient-to-r from-transparent via-gold-antique/30 to-transparent" />
        <span className="text-[9px] uppercase tracking-[0.5em] font-mono text-gold-antique/50">
          · Métricas ·
        </span>
        <span className="h-px flex-1 bg-gradient-to-r from-transparent via-gold-antique/30 to-transparent" />
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <MysticStat index={0} label="Usuarios totales"     value={stats?.total_users ?? "—"}                                  icon={Users}         accent="gold"    testid="stat-total-users" />
        <MysticStat index={1} label="Membresías activas"   value={stats?.active_memberships ?? "—"}                           icon={Crown}         accent="gold"    testid="stat-active-memberships" />
        <MysticStat index={2} label="Pagos por aprobar"    value={stats?.pending_payments ?? "—"}                             icon={Wallet}        accent="gold"    testid="stat-pending-payments" />
        <MysticStat index={3} label="Créditos circulantes" value={stats?.credits_in_circulation ?? "—"}                       icon={Coins}         accent="emerald" testid="stat-credits-circ" />
        <MysticStat index={4} label="Ingresos USD"         value={`$${stats?.revenue_total_usd?.toFixed(2) ?? "0.00"}`}       icon={DollarSign}    accent="emerald" testid="stat-revenue-usd" />
        <MysticStat index={5} label="Ingresos MXN"         value={`$${stats?.revenue_total_mxn?.toFixed(0) ?? "0"}`}          icon={DollarSign}    accent="emerald" testid="stat-revenue-mxn" />
        <MysticStat index={6} label="Mensajes hoy"         value={stats?.messages_today ?? "—"}                               icon={MessageSquare} accent="violet"  testid="stat-messages-today" />
        <MysticStat index={7} label="Nuevos hoy"           value={stats?.new_users_today ?? "—"}                              icon={UserPlus}      accent="violet"  testid="stat-new-users-today" />
        <MysticStat index={8} label="Descargas Desktop"    value={stats?.downloads_total ?? "—"}                              icon={Download}      accent="violet"
          suffix={stats?.downloads_today ? `+${stats.downloads_today} hoy` : null}
          testid="stat-downloads-total" />
      </div>

      {/* Gilded divider */}
      <div className="flex items-center gap-4">
        <span className="h-px flex-1 bg-gradient-to-r from-transparent via-gold-antique/30 to-transparent" />
        <span className="text-[9px] uppercase tracking-[0.5em] font-mono text-gold-antique/50">
          · Oráculo temporal ·
        </span>
        <span className="h-px flex-1 bg-gradient-to-r from-transparent via-gold-antique/30 to-transparent" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MysticChartCard
          subtitle="Últimos 14 días"
          title="Mensajes por día"
          icon={MessageSquare}
          testid="chart-messages"
        >
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="2 4" stroke="#3f2a55" strokeOpacity={0.3} vertical={false} />
              <XAxis dataKey="date" stroke="#52525b" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d?.slice?.(5) ?? d} />
              <YAxis stroke="#52525b" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "rgba(2,4,10,0.95)", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 6, fontSize: 12, boxShadow: "0 0 24px rgba(212,175,55,0.2)" }}
                labelStyle={{ color: "#D4AF37", fontFamily: "JetBrains Mono", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.15em" }}
                itemStyle={{ color: "#f4f4f5" }}
              />
              <Line type="monotone" dataKey="messages" stroke="#D4AF37" strokeWidth={2} dot={{ r: 3, fill: "#D4AF37", stroke: "#EED597", strokeWidth: 1 }} activeDot={{ r: 5, fill: "#EED597" }} />
            </LineChart>
          </ResponsiveContainer>
        </MysticChartCard>

        <MysticChartCard
          subtitle="Últimos 14 días"
          title="Ingresos por día (USD)"
          icon={DollarSign}
          testid="chart-revenue"
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="2 4" stroke="#3f2a55" strokeOpacity={0.3} vertical={false} />
              <XAxis dataKey="date" stroke="#52525b" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d?.slice?.(5) ?? d} />
              <YAxis stroke="#52525b" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
              <Tooltip
                contentStyle={{ background: "rgba(2,4,10,0.95)", border: "1px solid rgba(109,40,217,0.4)", borderRadius: 6, fontSize: 12, boxShadow: "0 0 24px rgba(109,40,217,0.2)" }}
                labelStyle={{ color: "#8b5cf6", fontFamily: "JetBrains Mono", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.15em" }}
                itemStyle={{ color: "#f4f4f5" }}
              />
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#8b5cf6" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#4C1D95" stopOpacity={0.6} />
                </linearGradient>
              </defs>
              <Bar dataKey="revenue_usd" fill="url(#revenueGradient)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </MysticChartCard>
      </div>
    </div>
  );
}
